import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import AuthReducer, { ACTIONS } from "./AuthReducer";
import type { Session, User } from "@supabase/supabase-js";
import type { IAuthContextType } from "./types";

const AuthContext = createContext<IAuthContextType | undefined>(undefined);

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(AuthReducer, {
    session: null,
    user: null,
    loading: true,
    initialAuthChecked: false,
    recoveryPassword: false,
  });

  const handleSetUser = useCallback(
    (user: User | null) => dispatch({ type: ACTIONS.SET_USER, payload: user }),
    [dispatch],
  );

  const handleSetSession = useCallback(
    (session: Session | null) =>
      dispatch({ type: ACTIONS.SET_SESSION, payload: session }),
    [],
  );

  const handleSetInitialAuthChecked = useCallback(
    (status: boolean) =>
      dispatch({ type: ACTIONS.SET_INITIAL_AUTH_CHECKED, payload: status }),
    [],
  );

  const handleSetLoading = useCallback(
    (status: boolean) =>
      dispatch({ type: ACTIONS.SET_LOADING, payload: status }),
    [],
  );

  const handleSetInitialSession = useCallback(
    (session: Session | null, user: User | null) => {
      dispatch({
        type: ACTIONS.SET_INITIAL_SESSION,
        payload: {
          session,
          user,
          loading: false,
          initialAuthChecked: true,
        },
      });
    },
    [],
  );

  const handleSetRecoveryPassword = useCallback((statusMode: boolean) => {
    dispatch({
      type: ACTIONS.SET_RECOVERY_PASSWORD,
      payload: statusMode,
    });
  }, []);

  const handleInitUpdatePasswordFlow = useCallback(() => {
    dispatch({
      type: ACTIONS.SET_INIT_UPDATE_PASSWORD_FLOW,
    });
  }, []);

  const value = useMemo(
    () => ({
      session: state.session,
      user: state.user,
      loading: state.loading,
      initialAuthChecked: state.initialAuthChecked,
      recoveryPassword: state.recoveryPassword,
      handleSetUser,
      handleSetSession,
      handleSetLoading,
      handleSetInitialSession,
      handleSetInitialAuthChecked,
      handleSetRecoveryPassword,
      handleInitUpdatePasswordFlow,
    }),
    [
      state.user,
      state.session,
      state.loading,
      state.initialAuthChecked,
      state.recoveryPassword,
      handleSetUser,
      handleSetSession,
      handleSetLoading,
      handleSetInitialSession,
      handleSetRecoveryPassword,
      handleSetInitialAuthChecked,
      handleInitUpdatePasswordFlow,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
